import { Component, Input, OnInit } from '@angular/core';
import {
  differenceInDays,
  format,
  formatISODuration,
  intervalToDuration,
  isSameDay,
} from 'date-fns';
import { thresholdCalculator } from '~/core/helpers/threshold/threshold-calculator.helper';

import { Activity } from '~/core/model/activity.model';
import { ActivityTypeEnum } from '@sport-activities/sdk';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { Timeslot } from '~/core/model/timeslot.model';
import { TimeslotVariantEnum } from '~/component/timeslot/enums/variant.enum';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ToggleService } from '~/toggles/services/toggle.service';
import { BadgeVariantEnum } from '../badge/enums/variant.enum';
import { VtmnPlayIconTypeEnum } from '../vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-timeslot',
  templateUrl: './timeslot.component.html',
  styleUrls: ['./timeslot.component.scss'],
})
export class TimeslotComponent implements OnInit {
  @Input() public activity: Activity;
  @Input() public timeslot: Timeslot;
  @Input() public variant = TimeslotVariantEnum.Primary;
  @Input() public containerClass: string | string[];

  private endDate: Date;
  private startDate: Date;

  public readonly icon = VtmnPlayIconTypeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly yellowVariant = BadgeVariantEnum.YELLOW;

  constructor(
    private readonly checkoutService: CheckoutService,
    private readonly localeService: LocaleService,
    private readonly toggleService: ToggleService,
    private readonly translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.startDate = new Date(this.timeslot.startDate);
    this.endDate = new Date(this.timeslot.endDate);
  }

  public get isStarted(): boolean {
    return this.startDate < new Date();
  }

  public get isTimeslotInCart(): boolean {
    return this.checkoutService.cartHasItem(this.timeslot);
  }

  public get capacityClass(): string {
    const remainingCapacityPercentage =
      (this.timeslot.remainingAttendeeCapacity /
        this.timeslot.maximumAttendeeCapacity) *
      100;
    return thresholdCalculator(remainingCapacityPercentage, [
      {
        threshold: 0,
        value: 'text-vtmn-play-status-negative',
      },
      {
        threshold: 15,
        value: 'text-vtmn-play-status-warning',
      },
      {
        threshold: 60,
        value: 'text-vtmn-play-status-positive',
      },
    ]);
  }

  public get duration(): string {
    return this.timeslot.formattedDuration(
      { locale: this.localeService.dateLocale },
      this.activity.type ===
        (('MULTI_DAY_COURSE' as ActivityTypeEnum) ||
          ('EVENT' as ActivityTypeEnum))
    );
  }

  public get isEvent(): boolean {
    return this.activity.type === ('EVENT' as ActivityTypeEnum);
  }

  public get formattedStartDate(): string {
    if (this.activity.type === 'EVENT') {
      const sameDay = isSameDay(this.endDate, this.startDate);
      const dayDifference = differenceInDays(this.endDate, this.startDate);
      const text = dayDifference === 1 ? 'timeslot.and' : 'timeslot.to';

      if (sameDay) {
        return format(this.startDate, 'EEE dd MMM y', {
          locale: this.localeService.dateLocale,
        });
      }

      const start = format(this.startDate, 'EEEE dd', {
        locale: this.localeService.dateLocale,
      });
      const end = format(this.endDate, 'EEEE dd MMM y', {
        locale: this.localeService.dateLocale,
      });
      return `${start} ${this.translateService.instant(text)} ${end}`;
    }

    return format(this.startDate, 'EEE dd MMM y', {
      locale: this.localeService.dateLocale,
    });
  }

  public get formattedStartTime(): string {
    if (this.isTimezoneSelectionEnabled && this.activity?.type === 'LIVE') {
      return format(new Date(this.timeslot.startDate), 'HH:mm (z)', {
        locale: this.localeService.dateLocale,
      });
    }

    const hourSet = this.setHours(
      new Date(this.startDate),
      this.timeslot?.startDateTimezone
    );

    return `${hourSet.getUTCHours().toString()}:${this.startDate.getUTCMinutes().toString().padStart(2, '0')}`;
  }

  public get ISODuration(): string {
    return formatISODuration(
      intervalToDuration({ start: this.startDate, end: this.endDate })
    );
  }

  public get rightColumnClass(): string {
    return this.variant === TimeslotVariantEnum.RightColCentered
      ? 'timeslot__column-centered'
      : '';
  }

  public get isTimezoneSelectionEnabled(): boolean {
    return this.toggleService.isEnabled(this.toggleNameEnum.TimezoneSelection);
  }

  public setHours(date: Date, hours) {
    date.setUTCHours(date.getUTCHours() + hours);
    return date;
  }
}
